import { useBlobPreview } from "hooks/useBlobPreview";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { useToast } from "hooks/useToast";
import { lazy, Suspense, useCallback, useState } from "react";
import { FileIcon } from "react-file-utils";
import ReactPlayer from "react-player";
import { useDispatch } from "react-redux";
import { setPreviewFiles } from "store/slices/chat";
import { DOCX, EXCEL_EXTENSIONS } from "constants/fileTypes";
import { fileTypeFromName } from "util/fileTypeFromName";
import { ChatPdfPreviewModal } from "./ChatPdfPreviewModal";
import { ChatSpreadsheetPreviewModal } from "./ChatSpreadsheetPreviewModal";
import { Gallery } from "./Gallery";
import { ScrappedContent } from "./ScrappedContent";
import { Image } from "./Image";
import { DownloadIconV2 } from "components/icons/DownloadIconV2";

const ChatWordPreviewModal = lazy(() =>
  import("./ChatWordPreviewModal").then((m) => ({
    default: m.ChatWordPreviewModal,
  }))
);

export type AttachmentType = {
  asset_url?: string;
  type?: string;
  image_url?: string;
  fallback?: string;
  title_link?: string;
  og_scrape_url?: string;
  title?: string;
  thumb_url?: string;
  mime_type?: string;
  original_height?: number;
  original_width?: number;
};

export type AttachmentProps = {
  attachments: AttachmentType[];
  sent: boolean;
};

const File = ({ file }: { file: AttachmentType }) => {
  const { preview } = useBlobPreview();
  const { alertToast } = useToast();
  const isSmallScreen = useIsSmallScreen();
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [excelPreviewUrl, setExcelPreviewUrl] = useState<string | null>(null);
  const [wordPreviewBlob, setWordPreviewBlob] = useState<Blob | null>(null);

  const fileExtension = fileTypeFromName(file.title ?? "");
  const isExcelFile = EXCEL_EXTENSIONS.includes(fileExtension);
  const isWordFile = fileExtension === DOCX;

  const closePdfPreview = () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
  };

  const closeExcelPreview = () => {
    if (excelPreviewUrl) URL.revokeObjectURL(excelPreviewUrl);
    setExcelPreviewUrl(null);
  };

  const closeWordPreview = useCallback(() => setWordPreviewBlob(null), []);

  const onPreview = async () => {
    if (!file.asset_url) return;
    try {
      const response = await preview({ url: file.asset_url });

      if (isExcelFile) {
        setExcelPreviewUrl(response?.fileURL ?? null);
        return;
      }

      if (isWordFile) {
        if (response?.fileURL) URL.revokeObjectURL(response.fileURL);
        setWordPreviewBlob(response?.blob ?? null);
        return;
      }

      if (file.mime_type !== "application/pdf") {
        window.open(file?.asset_url, "_self");
        return;
      }

      if (isSmallScreen) {
        setPdfPreviewUrl(response?.fileURL ?? null);
        return;
      }

      window.open(response?.fileURL, "_blank");
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        "Something went wrong";

      alertToast(
        { message },
        typeof error === "object" && error !== null ? error : undefined
      );
    }
  };

  return (
    <div className="all:unset t-flex t-items-center t-cursor-pointer t-gap-2 t-group/file t-relative">
      <div className="t-size-5 t-shrink-0">
        <FileIcon mimeType={file.mime_type} version="2" />
      </div>
      <button
        onClick={onPreview}
        className="all:unset t-break-all t-text-text-100 hover:t-text-purple"
      >
        {file.title}
      </button>
      <a
        download
        href={file?.asset_url}
        className="all:unset t-hidden group-hover/file:t-flex t-rounded-full t-size-5 t-shrink-0 t-items-center t-justify-center t-text-text-30 hover:t-text-purple"
      >
        <DownloadIconV2 className="t-size-5" />
      </a>
      <ChatPdfPreviewModal
        name={file.title ?? ""}
        url={pdfPreviewUrl}
        onClose={closePdfPreview}
      />
      <ChatSpreadsheetPreviewModal
        name={file.title ?? ""}
        url={excelPreviewUrl}
        onClose={closeExcelPreview}
      />
      {wordPreviewBlob && (
        <Suspense fallback={null}>
          <ChatWordPreviewModal
            name={file.title ?? ""}
            blob={wordPreviewBlob}
            onClose={closeWordPreview}
          />
        </Suspense>
      )}
    </div>
  );
};

export const Attachment = ({ attachments, sent }: AttachmentProps) => {
  const dispatch = useDispatch();
  const images = attachments.filter(
    (a) => a.type === "image" && !(a.og_scrape_url || a.title_link)
  );
  const isImage = images.length === 1;
  const image = images[0];
  const isGallery = images.length > 1;
  const videos = attachments.filter(
    (e) => e.type === "video" && !(e.og_scrape_url || e.title_link)
  );
  const audios = attachments.filter(
    (e) => e.type === "audio" && !(e.og_scrape_url || e.title_link)
  );
  const files = attachments.filter(
    (e) => e.type === "file" && !(e.og_scrape_url || e.title_link)
  );
  const scrappedContents = attachments.filter(
    (e) => e.og_scrape_url || e.title_link
  );

  const lastScrappedContent = scrappedContents.at(-1);

  const onImageClick = () => {
    dispatch(setPreviewFiles({ files: [image] }));
  };

  return (
    <>
      {lastScrappedContent && (
        <ScrappedContent sent={sent} attachment={lastScrappedContent} />
      )}
      {isGallery && <Gallery images={images} />}
      {isImage && (
        <div onClick={onImageClick}>
          <Image
            height={image.original_height || 266}
            width={image.original_width}
            src={image.image_url}
            alt={image.fallback}
            className="t-max-h-[266px] t-w-full t-rounded t-object-cover"
          />
        </div>
      )}
      {videos.length > 0 &&
        videos.map(({ asset_url }) => (
          <ReactPlayer
            key={asset_url}
            controls
            height="100%"
            url={asset_url}
            width="100%"
            className="t-rounded [&_video]:t-rounded"
          />
        ))}

      {audios.map(({ asset_url }) => (
        <audio controls className="t-rounded" key={asset_url}>
          <source data-testid="audio-source" src={asset_url} type="audio/mp3" />
        </audio>
      ))}

      {files.map((file) => (
        <File file={file} key={file.asset_url} />
      ))}
    </>
  );
};
